import React, { useState, useEffect } from 'react';
import { saveAthleteSettings, getAthleteSettings, getAllZones, deleteAthleteSettings } from '../../api/athlete';
import { useAuth } from '../../contexts/AuthContext';
import { AthleteSettings, AllZones } from '../../types/athlete';
import RunPaceInput from './RunPaceInput';
import { Save, Trash2, Info } from 'lucide-react';

const AthleteSettingsForm: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<Partial<AthleteSettings>>({});
  const [zones, setZones] = useState<AllZones | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!user) return;
    let isMounted = true;
    const loadSettings = async () => {
      try {
        const data = await getAthleteSettings(user.id);
        if (isMounted) setSettings(data || {});
        if (data) {
          const calculatedZones = await getAllZones(user.id);
          if (isMounted) setZones(calculatedZones);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadSettings();
    return () => { isMounted = false; };
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setMessage(null);
    try {
      await saveAthleteSettings(user.id, settings);
      const calculatedZones = await getAllZones(user.id);
      setZones(calculatedZones);
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!user) return;
    if (window.confirm('Are you sure you want to clear all your athlete settings?')) {
      try {
        await deleteAthleteSettings(user.id);
        setSettings({});
        setZones(null);
        setMessage({ type: 'success', text: 'Settings cleared' });
      } catch (error) {
        console.error('Error clearing settings:', error);
        setMessage({ type: 'error', text: 'Failed to clear settings' });
      }
    }
  };

  const handleChange = (field: keyof AthleteSettings, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Athlete Settings</h1>
          <p className="text-muted">Configure your metrics for automatic pace and zone calculations</p>
        </div>
        <button
          onClick={handleClear}
          className="flex items-center space-x-2 px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors"
        >
          <Trash2 size={18} />
          <span>Clear All</span>
        </button>
      </div>

      {message && (
        <div className={`p-4 rounded-lg mb-6 ${
          message.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-500' : 'bg-red-500/10 border border-red-500/20 text-red-500'
        }`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Swim Section */}
        <div className="card-base p-6">
          <h2 className="text-xl font-semibold mb-4">Swim Metrics</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
                CSS Value (sec/100m)
              </label>
              <input
                type="number"
                step="0.1"
                placeholder="e.g., 90.5"
                value={settings.css_value || ''}
                onChange={(e) => handleChange('css_value', e.target.value ? parseFloat(e.target.value) : null)}
                className="w-full"
              />
              <p className="text-sm text-muted mt-1">Critical Swim Speed. Lower is faster.</p>
            </div>
          </div>
        </div>

        {/* Bike Section */}
        <div className="card-base p-6">
          <h2 className="text-xl font-semibold mb-4">Bike Metrics</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
                FTP (Watts)
              </label>
              <input
                type="number"
                placeholder="e.g., 250"
                value={settings.cycling_ftp || ''}
                onChange={(e) => handleChange('cycling_ftp', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full"
              />
              <p className="text-sm text-muted mt-1">Functional Threshold Power</p>
            </div>
          </div>
        </div>

        {/* Run Section */}
        <div className="card-base p-6">
          <h2 className="text-xl font-semibold mb-4">Run Metrics</h2>
            <RunPaceInput
              value={{
                distance: settings.run_reference_distance || 5,
                distanceUnit: 'km',
                timeMinutes: (() => {
                  const parts = (settings.run_reference_time || '20:00').split(':');
                  return parseInt(parts[0]) || 20;
                })(),
                timeSeconds: (() => {
                  const parts = (settings.run_reference_time || '20:00').split(':');
                  return parseInt(parts[1]) || 0;
                })(),
              }}
              onChange={(val) => {
                const totalSeconds = val.timeMinutes * 60 + val.timeSeconds;
                const hours = Math.floor(totalSeconds / 3600);
                const minutes = Math.floor((totalSeconds % 3600) / 60);
                const seconds = totalSeconds % 60;
                // Always store as HH:MM:SS format for PostgreSQL TIME field
                const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                handleChange('run_reference_distance', val.distance);
                handleChange('run_reference_time', timeStr);
              }}
              showZones={true}
            />
        </div>

        {/* HR Section */}
        <div className="card-base p-6">
          <h2 className="text-xl font-semibold mb-4">Heart Rate Zones</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
                Max HR (bpm)
              </label>
              <input
                type="number"
                placeholder="e.g., 185"
                value={settings.max_hr || ''}
                onChange={(e) => handleChange('max_hr', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
                Resting HR (bpm)
              </label>
              <input
                type="number"
                placeholder="e.g., 50"
                value={settings.resting_hr || ''}
                onChange={(e) => handleChange('resting_hr', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
                Zone Method
              </label>
                <select
                  value={settings.hr_zone_method || ''}
                  onChange={(e) => handleChange('hr_zone_method', e.target.value || null)}
                  className="w-full"
                >
                  <option value="">Select method</option>
                  <option value="max_hr">Max HR Based</option>
                  <option value="karvonen">Karvonen (HR Reserve)</option>
                  <option value="joe_friel">Joe Friel's Zones</option>
                </select>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-8">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center space-x-2 px-6 py-3 bg-[#3b82f6] text-white rounded-lg hover:bg-[#60a5fa] transition-colors disabled:opacity-50"
        >
          <Save size={20} />
          <span>{saving ? 'Saving...' : 'Save Settings'}</span>
        </button>
      </div>

      {/* Zones Preview */}
      {zones && (
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Calculated Zones Preview</h2>

          {/* Run Paces */}
          {zones.run.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Run Paces</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {zones.run.map((zone) => (
                  <div key={zone.name} className="card-base p-4">
                    <p className="font-medium text-[#3b82f6]">{zone.name}</p>
                    <p className="text-sm text-muted">{zone.perKm}/km</p>
                    <p className="text-xs text-muted mt-1">{zone.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Swim Zones */}
          {zones.swim.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Swim Zones</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {zones.swim.map((zone) => (
                  <div key={zone.zone} className="card-base p-4">
                    <p className="font-medium text-[#3b82f6]">Zone {zone.zone}: {zone.name}</p>
                    <p className="text-sm text-muted">{zone.pacePer100m}/100m</p>
                    <p className="text-xs text-muted mt-1">{zone.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bike Power */}
          {zones.bike.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Bike Power Zones</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {zones.bike.map((zone) => (
                  <div key={zone.zone} className="card-base p-4">
                    <p className="font-medium text-[#3b82f6]">Zone {zone.zone}: {zone.name}</p>
                    <p className="text-sm text-muted">{zone.watts}</p>
                    <p className="text-xs text-muted mt-1">{zone.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* HR Zones */}
          {zones.hr.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Heart Rate Zones</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {zones.hr.map((zone) => (
                  <div key={zone.zone} className="card-base p-4">
                    <p className="font-medium text-[#3b82f6]">Zone {zone.zone}: {zone.name}</p>
                    <p className="text-sm text-muted">{zone.range} bpm</p>
                    <p className="text-xs text-muted mt-1">{zone.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {zones.run.length === 0 && zones.swim.length === 0 && zones.bike.length === 0 && zones.hr.length === 0 && (
            <div className="card-base p-8 text-center text-muted">
              <Info className="mx-auto mb-4" size={48} />
              <p>Enter your metrics above and save to see calculated zones</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AthleteSettingsForm;
